import {
    Alert,
    AnomalyAlertResult,
    InboxAlert,
    QueryTrigger,
    TriggerType,
    UbaAlertResult,
} from "@app2/clients/types/alert-typedefs";
import { SavedQuery } from "@app2/type-defs/search/search-types";
import { SortType } from "@app2/fraud/type-defs";

function escapePrimaryTerm(primaryTerm) {
    if (primaryTerm && /[:/\\]/g.test(primaryTerm)) {
        return primaryTerm.replace(/\\/g, "\\\\").replace(/\//g, "\\/");
    }
    return primaryTerm;
}

/**
 * This should produce a query that can be issued to search-service as text, and somewhat-accurately represent
 * the backend components of a QueryTrigger that were involved in creating the AlertResult.
 * @param trigger the QueryTrigger object that we're attempting to represent
 * @param savedQuery the SavedQuery that is referenced by trigger's savedQueryId in search-service
 * @param alertResult an AlertResult generated by trigger in alert-service
 */
export function queryStringForAlertResult(trigger: QueryTrigger,
                                          savedQuery: SavedQuery,
                                          alertResult: Alert | InboxAlert | AnomalyAlertResult | UbaAlertResult) {
    // Where's the sequence trigger logic? Don't call with sequence trigger

    if (trigger.type === TriggerType.threat) {
        return getMatchedThreatValue(alertResult as Alert | InboxAlert);
    }

    if (trigger.type === TriggerType.fraudUba) {
        return "app_name:(apiture audit) -defensestorm_type:alert " + alertResult.primaryTerm;
    }

    // Shouldn't get hit unless used with a trigger without a savedQueryId or an invalid one
    // which is hopefully never
    if (!savedQuery) {
        return "";
    }

    const queryParts = [`(${savedQuery.query})`];

    if (savedQuery.primaryField && alertResult.primaryTerm) {
        const escapedTerm = escapePrimaryTerm(alertResult.primaryTerm);
        queryParts.push(`${savedQuery.primaryField}:"${escapedTerm}"`);
    }

    // if the trigger is an EveryEvent trigger, the result_timestamp tells us about the `ingest_timestamp` rather
    // than the `timestamp`. Add an additional queryPart to restrict the results to the `ingest_timestamp` that
    // we're interested in, since we making `timestamp` much to far in both directions.
    if (trigger.everyEvent) {
        queryParts.push(`ingest_timestamp:[${alertResult.resultTimestamp} TO ${alertResult.resultEndTimestamp}]`);
    }

    if (queryParts.length === 1) {
        // if we didn't add anything, get rid of the parens and just use the base query string
        return savedQuery.query;
    } else {
        return queryParts.join(" AND ");
    }
}

export function getMatchedThreatValue(alert: Alert | InboxAlert): string {
    if (alert.host) {
        return alert.host;
    } else if (alert.signature) {
        return alert.signature;
    } else {
        return alert.location;
    }
}

export function getSort(sortType: SortType): { field: string, desc: boolean } {
    switch (sortType) {
        case "newest_first":
            return { field: "TIMESTAMP", desc: true };
        case "oldest_first":
            return { field: "TIMESTAMP", desc: false };
        case "highest_severity":
            return { field: "SEVERITY", desc: true };
        case "lowest_severity":
            return { field: "SEVERITY", desc: false };
        case "last_escalated_desc":
            return { field: "LAST_ESCALATED", desc: true };
        case "last_escalated_asc":
            return { field: "LAST_ESCALATED", desc: false };
        case "last_dismissed_desc":
            return { field: "LAST_DISMISSED", desc: true };
        case "last_dismissed_asc":
            return { field: "LAST_DISMISSED", desc: false };
        case "last_fraud_flagged_desc":
            return { field: "LAST_FRAUD_FLAGGED", desc: true };
        case "last_fraud_flagged_asc":
            return { field: "LAST_FRAUD_FLAGGED", desc: false };
    }
}

export function getOverallScoreForUbaAlert(alert: UbaAlertResult): number {
    return alert.riskFactors?.map(riskFactor => riskFactor.scoreImpact)
        .reduce((a, b) => a + b, 0);
}
